import {
  computeLocalEntityPoseFromWorldTransforms,
  RevisionTransformCache,
} from "@/data-preparation-tool/store/revision/revision-transform-cache";
import { RootState } from "@/store/store";
import { GUID } from "@faro-lotv/foundation";
import {
  CaptureTreeEntityRevision,
  CaptureTreeEntityType,
  CaptureTreeEntityTypeHint,
  CreateOrUpdateClusterEntityParams,
  CreateOrUpdateScanEntityParams,
  isSiteScapeClusterEntity,
  ProjectApi,
  RegistrationRevision,
} from "@faro-lotv/service-wires";
import type { NodeApi } from "react-arborist";
import { Matrix4 } from "three";
import { applyRevision } from "../../editing/apply-revision";
import { revisionToEdit } from "../../inspect-and-publish/refine-and-publish-utils";
import { EntityMap } from "../../store/revision/revision-slice";

/**
 * @param entity Entity to check.
 * @returns true if the entity is a special capture cluster that should not be used as a drop target.
 */
function isSpecialCaptureCluster(entity: CaptureTreeEntityRevision): boolean {
  return (
    (entity.type === CaptureTreeEntityType.cluster &&
      entity.typeHint === CaptureTreeEntityTypeHint.orbisCapture) ||
    isSiteScapeClusterEntity(entity)
  );
}

/**
 * @param scanId ID of the scan to check.
 * @param entityMap Map of all entities by ID.
 * @returns true if the scan is the child of a special capture cluster.
 */
function isScanInSpecialCluster(scanId: GUID, entityMap: EntityMap): boolean {
  const entity = entityMap[scanId];
  if (!entity || !entity.parentId) {
    return false;
  }
  const parent = entityMap[entity.parentId];
  return parent ? isSpecialCaptureCluster(parent) : false;
}

/**
 * @param entityId ID of the entity to check.
 * @param ancestorId ID of the potential ancestor.
 * @param entityMap Map of all entities by ID.
 * @returns true if entityId is a descendant of ancestorId
 */
function isDescendantOf(
  entityId: GUID,
  ancestorId: GUID,
  entityMap: EntityMap,
): boolean {
  let current = entityMap[entityId];
  while (current && current.parentId) {
    if (current.parentId === ancestorId) {
      return true;
    }
    current = entityMap[current.parentId];
  }
  return false;
}

/**
 * @param dragSources All entities currently in the drag selection.
 * @param entityMap Map of all entities by ID.
 * @returns true if the entity can be dragged given the provided drag selection.
 *   A scan inside a special capture cluster is considered acceptable when the parent
 *   cluster is also in the drag selection, because the scan will be carried along
 *   implicitly and filtered out by excludeDescendantsOfDraggedEntities.
 */
export function areAllDragSourcesValid(
  dragSources: CaptureTreeEntityRevision[],
  entityMap: EntityMap,
): boolean {
  function isDragSourceEffectivelyValid(
    dragSource: CaptureTreeEntityRevision,
  ): boolean {
    return (
      (!!dragSource.parentId &&
        !isScanInSpecialCluster(dragSource.id, entityMap)) ||
      dragSources.some((other) => other.id === dragSource.parentId)
    );
  }

  return dragSources.every((dragSource) =>
    isDragSourceEffectivelyValid(dragSource),
  );
}

/**
 * @param dropNode Node of the drop target to check.
 * @param draggedIds IDs of the dragged entities.
 * @param entityMap Map of all entities by ID.
 * @returns true if the entity is a valid drop target for the dragged entities.
 */
export function isValidDropTarget(
  dropNode: NodeApi<CaptureTreeEntityRevision> | null,
  draggedIds: GUID[],
  entityMap: EntityMap,
): boolean {
  // Always allow dropping into the menu's root node, which doesn't correspond to any entity.
  // In this case, the dragged entities will be reparented to the root entity.
  if (
    dropNode === null ||
    dropNode.level === -1 ||
    dropNode.data.id === "__REACT_ARBORIST_INTERNAL_ROOT__"
  ) {
    return true;
  }
  const dropTarget = dropNode.data;
  // Can't drop into special capture clusters.
  // Can't drop into itself.
  if (
    isSpecialCaptureCluster(dropTarget) ||
    draggedIds.includes(dropTarget.id)
  ) {
    return false;
  }
  // Can't drop into any of the dragged entities' descendants.
  for (const draggedId of draggedIds) {
    if (isDescendantOf(dropTarget.id, draggedId, entityMap)) {
      return false;
    }
  }
  // If for all dragged entities the drop target is its current parent, then the drop is
  // effectively a no-op and should be disallowed.
  if (
    draggedIds.every((draggedId) => {
      const draggedEntity = entityMap[draggedId];
      return draggedEntity?.parentId === dropTarget.id;
    })
  ) {
    return false;
  }

  return true;
}

/**
 * @param draggedEntities Dragged entities.
 * @param entityMap Map of all entities by ID.
 * @returns Dragged entities with all those removed that are descendants of other dragged entities.
 */
export function excludeDescendantsOfDraggedEntities(
  draggedEntities: CaptureTreeEntityRevision[],
  entityMap: EntityMap,
): CaptureTreeEntityRevision[] {
  return draggedEntities.filter(
    (draggedEntity) =>
      !draggedEntities.some(
        (other) =>
          other.id !== draggedEntity.id &&
          isDescendantOf(draggedEntity.id, other.id, entityMap),
      ),
  );
}

/**
 * Calculates new local poses for the dragged entities when moving to the new parent.
 *
 * @param dropTargetId ID of the drop target, the new parent.
 * @param draggedIds IDs of the dragged entities.
 * @param transformCache World transform cache delivered by selectDataPreparationTransformCache.
 * @returns The new local poses and world transform cache for the affected entities.
 *   null if there is a problem and the move should be aborted.
 */
export function calculateNewPosesForMove(
  dropTargetId: GUID,
  draggedIds: GUID[],
  transformCache: RevisionTransformCache,
): Record<GUID, CaptureTreeEntityRevision["pose"] | undefined> | null {
  const worldTransformOfDropTarget = transformCache[dropTargetId];
  if (!worldTransformOfDropTarget) {
    return null;
  }
  const mat4OfDropTarget = new Matrix4().fromArray(
    worldTransformOfDropTarget.worldMatrix,
  );

  const newPoses: Record<GUID, CaptureTreeEntityRevision["pose"] | undefined> =
    {};

  for (const draggedId of draggedIds) {
    const worldTransformOfDraggedEntity = transformCache[draggedId];
    if (!worldTransformOfDraggedEntity) {
      return null;
    }
    const mat4OfDraggedEntity = new Matrix4().fromArray(
      worldTransformOfDraggedEntity.worldMatrix,
    );

    newPoses[draggedId] = computeLocalEntityPoseFromWorldTransforms(
      mat4OfDraggedEntity,
      mat4OfDropTarget,
    );
  }

  return newPoses;
}

type SaveMovedEntitiesParams = {
  /** The Project API client used to persist the changes. */
  projectApiClient: ProjectApi;
  /** The current application state. */
  state: RootState;
  /** The revision the moved entities belong to. */
  revision: RegistrationRevision;
  /** The entities that were moved to the new parent. */
  movedEntities: CaptureTreeEntityRevision[];
  /** The ID of the new parent the entities were moved to. */
  newParentId: GUID;
  /** The new local poses of the moved entities, keyed by entity ID. */
  newPoses: Record<GUID, CaptureTreeEntityRevision["pose"] | undefined>;
};

/**
 * Persists a move of clusters and scans to a new parent in the Draft view's scan tree, using the
 * same approach as for renaming entities in renameEntity.
 *
 * @param params Parameters describing the move.
 */
export async function saveMovedEntities({
  projectApiClient,
  state,
  revision,
  movedEntities,
  newParentId,
  newPoses,
}: SaveMovedEntitiesParams): Promise<void> {
  const editRevision = await revisionToEdit(revision, projectApiClient, state);

  const updatedClusters: CreateOrUpdateClusterEntityParams[] = [];
  const updatedScans: CreateOrUpdateScanEntityParams[] = [];

  for (const entity of movedEntities) {
    const updateData = {
      parentId: newParentId,
      pose: newPoses[entity.id] ?? entity.pose,
    };

    if (entity.type === CaptureTreeEntityType.cluster) {
      updatedClusters.push({ ...entity, ...updateData });
    } else {
      updatedScans.push({ ...entity, ...updateData });
    }
  }

  if (updatedClusters.length) {
    await projectApiClient.createOrUpdateClusterEntitiesForRegistrationRevision(
      {
        registrationRevisionId: editRevision.id,
        requestBody: updatedClusters,
      },
    );
  }

  if (updatedScans.length) {
    await projectApiClient.createOrUpdateScanEntitiesForRegistrationRevision({
      registrationRevisionId: editRevision.id,
      requestBody: updatedScans,
    });
  }

  await applyRevision({ revision: editRevision, projectApiClient });
}
